/**
 * The drawer's small form parts — the concept's `fe-field`, `fe-seg`,
 * `fe-fs` and `fe-callout`, in Tailwind.
 *
 * Native `<select>` and `<input>` rather than `@wend/ui`'s Radix Select on
 * purpose: the drawer holds a dozen of them in a 372px column, several inside
 * one rule row, and a Radix popover per control inside a scrolling drawer
 * inside a scaled canvas page is a focus-trap problem the native control does
 * not have. They are styled to the same tokens, and the one place the design
 * system's own control fits — the two- to four-way choices — uses its
 * `SegmentedToggle`.
 */
import { cn } from '@wend/ui';
import { useEffect, useState, type ReactNode } from 'react';

export const CTRL = cn(
  'w-full min-w-0 rounded-lg border border-line-strong bg-white/5 px-2.5 py-2 text-[13.5px] text-text',
  '[color-scheme:dark] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-b',
  '[&>option]:bg-surface-2 [&>optgroup]:bg-surface-2',
);

export const KICK = 'm-0 font-mono text-[10.5px] uppercase tracking-[0.1em] text-muted';
export const HINT = 'm-0 text-[12px] leading-snug text-muted';

export function Field({ label, hint, children, className }: { label: ReactNode; hint?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <label className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <span className={KICK}>{label}</span>
      {children}
      {hint && <span className={HINT}>{hint}</span>}
    </label>
  );
}

export function Fieldset({ legend, children }: { legend: string; children: ReactNode }) {
  return (
    <fieldset className="m-0 flex min-w-0 flex-col gap-2.5 rounded-xl border border-line p-3">
      <legend className={cn(KICK, 'px-1.5')}>{legend}</legend>
      {children}
    </fieldset>
  );
}

export function CalloutNote({ children }: { children: ReactNode }) {
  return <p className="m-0 rounded-[10px] border border-brand-b/30 bg-brand-a/10 px-3 py-2.5 text-[12.5px] leading-snug text-muted">{children}</p>;
}

/**
 * A whole-number input that never hands the form a NaN. It keeps its own
 * text while being typed in, so clearing it to type "30" does not snap back
 * to the minimum after the first keystroke; out-of-range values are clamped
 * when focus leaves.
 */
export function IntInput({
  value,
  onChange,
  min,
  max,
  className,
  ...rest
}: {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  className?: string;
  'aria-label'?: string;
  id?: string;
}) {
  const [text, setText] = useState(String(value));
  useEffect(() => setText(String(value)), [value]);
  return (
    <input
      {...rest}
      type="number"
      inputMode="numeric"
      min={min}
      max={max}
      value={text}
      className={cn(CTRL, className)}
      onChange={(event) => {
        setText(event.target.value);
        const parsed = Number.parseInt(event.target.value, 10);
        if (Number.isFinite(parsed) && parsed >= min && parsed <= max) onChange(parsed);
      }}
      onBlur={() => {
        const parsed = Number.parseInt(text, 10);
        const clamped = Number.isFinite(parsed) ? Math.min(max, Math.max(min, parsed)) : value;
        setText(String(clamped));
        if (clamped !== value) onChange(clamped);
      }}
    />
  );
}

/** A number (decimals allowed) for rule values. */
export function NumInput({ value, onChange, className, ...rest }: { value: number; onChange: (value: number) => void; className?: string; 'aria-label'?: string }) {
  const [text, setText] = useState(String(value));
  useEffect(() => setText((current) => (Number(current) === value ? current : String(value))), [value]);
  return (
    <input
      {...rest}
      type="number"
      value={text}
      className={cn(CTRL, className)}
      onChange={(event) => {
        setText(event.target.value);
        const parsed = Number(event.target.value);
        if (event.target.value.trim() !== '' && Number.isFinite(parsed)) onChange(parsed);
      }}
    />
  );
}
